import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ExternalLink, Target } from "lucide-react";
import { toast } from "sonner";
import { scoreColor } from "./ProjectScoreCard";

type Match = {
  title: string; source: string; source_type: string; amount: string;
  deadline: string; url?: string; description?: string; eligibility?: string;
  fit_score: number; rationale: string;
};

const CACHE_KEY = (id: string) => `matched-grants-${id}`;

const MatchedGrants = ({ projectId, onScoreAgainst }: { projectId: string; onScoreAgainst: (g: Match) => void }) => {
  const fetchMatches = async (): Promise<Match[]> => {
    const cached = sessionStorage.getItem(CACHE_KEY(projectId));
    if (cached) return JSON.parse(cached);
    const { data, error } = await supabase.functions.invoke("match-grants-to-project", {
      body: { project_id: projectId },
    });
    if (error) throw error;
    const matches = (data?.matches ?? []) as Match[];
    sessionStorage.setItem(CACHE_KEY(projectId), JSON.stringify(matches));
    return matches;
  };

  const { data: matches, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["matched-grants", projectId],
    queryFn: fetchMatches,
    staleTime: Infinity,
  });

  const refresh = () => {
    sessionStorage.removeItem(CACHE_KEY(projectId));
    refetch();
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold">Matched Grants</h3>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching} className="gap-2">
          {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </Button>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Finding the best grants for your project…
        </div>
      ) : !matches?.length ? (
        <p className="text-sm text-muted-foreground">No matches yet. Try refreshing.</p>
      ) : (
        <div className="space-y-3">
          {matches.map((m, i) => (
            <div key={i} className="border border-border rounded-lg p-4 hover:border-primary/40 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <h4 className="font-semibold text-sm leading-tight">{m.title}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">{m.source}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-display text-2xl font-bold ${scoreColor(m.fit_score)}`}>{m.fit_score}</div>
                  <div className="text-[10px] text-muted-foreground">fit</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{m.rationale}</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-[10px]">{m.source_type}</Badge>
                <Badge variant="outline" className="text-[10px]">{m.amount}</Badge>
                <Badge variant="outline" className="text-[10px]">{m.deadline}</Badge>
              </div>
              <div className="flex items-center justify-between mt-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => onScoreAgainst(m)} className="text-xs">
                  Deep fit analysis
                </Button>
                {m.url && (
                  <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                    Apply <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchedGrants;